import { getBatchPlaceById, getPlaceById, getPlaceBySlug, getPlaceByLatLong, getPlaceAncestorsById, getSpecificPlaces, getDestinationImageId } from 'api/search'
import { getTagsByDestinationId } from 'api/tags'
import { API_CALL } from './actionConstants'
import { FETCH_PLACE_BY_NAME, FETCH_PLACE_BY_COORDS, FETCH_PLACE, FETCH_BATCH_PLACES, FETCH_PLACE_ANCESTORS, FETCH_SPECIFC_PLACES, FETCH_DESTINATION_IMAGE, FETCH_GOOGLE_PLACE, FETCH_DESTINATION_TAGS } from './apiActionConstants'
import type { AppAction } from './types/ActionTypes'
import { getGooglePlaceDetail } from 'tripPlanner/api/places'
import { getPlaceKey } from 'lib/geo/geoUtils'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { shouldSkipPlaceSlugLookup } from 'lib/search/shouldSkipPlaceSlugLookup'
import { getPlaceCachePartition } from 'lib/places/placeCacheUtils'
import { getActiveLocale } from 'selectors/placeSelectors'

export function fetchPlaceById(id: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const locale = getActiveLocale(state)
    const cacheEntry = getPlaceCachePartition(state.destination.placeCacheByLocale, locale)[id]
    if (cacheEntry) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PLACE,
      request: (): Promise<App.Place | null> =>
        getPlaceById(id).then(result => result?.id ? result : null),
      id,
      locale,
    })
  }
}

export function fetchBatchPlacesByIds(ids: ReadonlyArray<string>): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const locale = getActiveLocale(state)
    const partition = getPlaceCachePartition(state.destination.placeCacheByLocale, locale)
    const missing = unique(ids.filter(Boolean)).filter(id => !partition[id])
    if (missing.length === 0) return

    dispatch({
      type: API_CALL,
      api: FETCH_BATCH_PLACES,
      request: (): Promise<Array<App.Place>> => getBatchPlaceById(missing),
      ids: missing,
      locale,
    })
  }
}

export function fetchPlaceAncestorsById(id: string): AppAction {
  return (dispatch, getState) => {
    if (!id) {
      // Guard against empty/undefined placeId to avoid hitting
      // /api/search/place/v1/places//ancestors (double slash -> 404).
      return
    }

    const state = getState()
    const fetching = state.destination.fetchingAncestors[id]
    // Keep cached results and completed attempts, including failures.
    if (state.destination.placeAncestors[id] || fetching !== undefined) return

    dispatch({
      type: API_CALL,
      api: FETCH_PLACE_ANCESTORS,
      request: () => getPlaceAncestorsById(id),
      id,
    })
  }
}

export function fetchPlaceByName(location: string): AppAction {
  return (dispatch, getState) => {
    if (!location) {
      return
    }

    const state = getState()
    if (state.destination.placesLoading[location] || state.destination.locationToPlace[location] !== undefined) {
      return
    }

    // Scrapers append Cloudinary transforms onto /hotels/location paths; those
    // slugs always 404 — resolve null locally so we never call svc-search.
    if (shouldSkipPlaceSlugLookup(location)) {
      dispatch({
        type: API_CALL,
        api: FETCH_PLACE_BY_NAME,
        request: () => Promise.resolve(null),
        location,
      })
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PLACE_BY_NAME,
      // We use null here to explicitly set if we looked for it and didn't get any results
      request: () => getPlaceBySlug(location).then(result => {
        if (!result.id) {
          return null
        }
        return result
      }).catch((): null => null),
      location,
    })
  }
}

export function fetchDestinationImageId(placeId: string): AppAction {
  return (dispatch, getState) => {
    if (!placeId) {
      return
    }

    const state = getState()

    if (
      state.destination.fetchingDestinationImage[placeId] !== undefined ||
      state.destination.places[placeId]?.imageId
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_DESTINATION_IMAGE,
      request: () => getDestinationImageId(placeId),
      placeId,
    })
  }
}

interface PlaceByGeoCoordsOptions {
  level?: App.PlaceType
  levels?: Array<App.PlaceType>
}

export function fetchPlaceByGeoCoords(latitude: number, longitude: number, options?: PlaceByGeoCoordsOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getPlaceKey(latitude, longitude, options?.levels)
    if (
      state.destination.placesLoading[key] ||
      state.destination.coordsToPlace[key] ||
      state.destination.coordsToPlace[key] === null
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PLACE_BY_COORDS,
      request: async() => {
        if (options?.levels?.length) {
          for (const level of options.levels) {
            try {
              const result = await getPlaceByLatLong(latitude, longitude, level)
              return result
            } catch {
              // do nothing
            }
          }
          return null
        } else {
          return getPlaceByLatLong(latitude, longitude, options?.level).then(result => {
            if (!result.id) {
              return null
            }
            return result
          }).catch((): null => null)
        }
      },
      key,
    })
  }
}

export function fetchSpecificPlaces(placeId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const specificPlaces = state.destination.specificPlaces[placeId]
    const fetching = state.destination.fetchingSpecificPlaces[placeId]
    if (specificPlaces || fetching) {
      return
    }

    dispatch(({
      type: API_CALL,
      api: FETCH_SPECIFC_PLACES,
      request: () => getSpecificPlaces(placeId),
      placeId,
    }))
  }
}

export function fetchGooglePlaceById(googlePlaceId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.destination.placesLoading[googlePlaceId] ||
      state.destination.googlePlaces[googlePlaceId]
    ) {
      // already have it or are currently fetching it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_GOOGLE_PLACE,
      request: () => getGooglePlaceDetail({
        placeId: googlePlaceId,
        fields: ['geometry', 'formatted_address', 'address_components'],
      }).then((result): App.GooglePlace => {
        return {
          id: googlePlaceId,
          address: result.formatted_address!,
          latitude: result.geometry?.location.lat!,
          longitude: result.geometry?.location.lng!,
          addressDetails: {
            streetAddress: nonNullable([
              result.address_components?.find(component => component.types.includes('street_number'))?.long_name,
              result.address_components?.find(component => component.types.includes('route'))?.long_name,
            ]).join(' '),
            unitNumber: result.address_components?.find(component => component.types.includes('subpremise'))?.long_name,
            city: result.address_components?.find(component => component.types.includes('locality'))?.long_name ?? '',
            state: result.address_components?.find(component => component.types.includes('administrative_area_level_1'))?.short_name ?? '',
            country: result.address_components?.find(component => component.types.includes('country'))?.long_name ?? '',
            postcode: result.address_components?.find(component => component.types.includes('postal_code'))?.long_name ?? '',
          },
        }
      }),
      id: googlePlaceId,
    })
  }
}

interface FetchDestinationTagsOptions {
  travelStartDate?: string
  travelEndDate?: string
  travelDateOverlap?: boolean
}

export function fetchDestinationTags(destinationId: string, options: FetchDestinationTagsOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { travelStartDate, travelEndDate, travelDateOverlap } = options

    const datePart = [travelStartDate, travelEndDate].filter(Boolean).join(':')
    const cacheKey = datePart ? `${destinationId}:${datePart}` : destinationId

    const tagData = state.destination.destinationTags[cacheKey]
    // Skip if already fetched or currently fetching
    if (tagData) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_DESTINATION_TAGS,
      cacheKey,
      request: () => getTagsByDestinationId({
        destinationId,
        region: state.geo.currentRegionCode,
        travelStartDate,
        travelEndDate,
        travelDateOverlap,
      }).then(result => result.tags),
    })
  }
}
